import {
  XPlatformIcon,
  InstagramPlatformIcon,
  TiktokPlatformIcon,
  YoutubePlatformIcon,
  LinkedInPlatformIcon,
  FaceBookPlatformIcon
} from '@/components/common/icon';
import toast from 'react-hot-toast';
import { AdsPlatform } from '@/interface/order';
import {
  youtubeLinkValidator,
  tiktokLinkValidator,
  instagramReelsLinkValidator,
  twitterLinkValidator,
  linkValidator
} from '@/biz/validator';
import {
  submitModalDescDefault,
  submitModalDescYoutube,
  submitModalDescTiktok,
  submitModalDescInstagram
} from '@/components/web/config';
import {
  PlatformItems,
  LinkedInContentType,
  LinkedInContentTypeLabel
} from '@/interface/common';
import i18n from '@/i18n';

export const getPlatformName = (platform: string) => {
  platform = platform?.toLowerCase();
  // 处理大小写
  if (PlatformItems.YOUTUBE.toLowerCase() === platform) return 'YouTube';
  if (PlatformItems.TIKTOK.toLowerCase() === platform) return 'TikTok';
  if (PlatformItems.INSTAGRAM.toLowerCase() === platform) return 'Instagram';
  if (PlatformItems.TWITTER.toLowerCase() === platform) return 'Twitter';
  if (PlatformItems.LINKEDIN.toLowerCase() === platform) return 'LinkedIn';
  return `${platform?.charAt(0).toUpperCase()}${platform?.slice(1)}`;
};

export const getPlatformVideoContentType = ({
  platform,
  contentType
}: {
  platform: string;
  contentType?: string;
}) => {
  if (contentType === 'Dedicated video' || contentType === 'Integrated video') {
    return contentType;
  }
  if (
    contentType === 'Post' &&
    PlatformItems.INSTAGRAM.toLowerCase() === platform
  ) {
    return 'Carousel post';
  }
  //
  if (PlatformItems.YOUTUBE.toLowerCase() === platform)
    return 'Dedicated video';
  if (PlatformItems.TIKTOK.toLowerCase() === platform) return 'Tiktok video';
  if (PlatformItems.INSTAGRAM.toLowerCase() === platform) return 'Reels';
  if (PlatformItems.TWITTER.toLowerCase() === platform) return 'Post';
  if (PlatformItems.LINKEDIN.toLowerCase() === platform) {
    return (
      LinkedInContentTypeLabel[contentType as LinkedInContentType] || 'Post'
    );
  }
  return contentType || 'Post';
};

export const getPlatformVideoContentTypeDesc = (
  key: string,
  platform?: string
) => {
  const map: any = {
    'Dedicated video': i18n.t('platform.content_type.dedicated_video', {
      defaultValue:
        'A standalone YouTube video created specifically to focus entirely on promoting the brand'
    }),
    'Integrated video': i18n.t('platform.content_type.integrated_video', {
      defaultValue:
        'A YouTube video that seamlessly incorporates brand promotion within your regular content'
    }),
    Reels: i18n.t('platform.content_type.reels', {
      defaultValue:
        'A short Instagram video created to exclusively promote a brand'
    }),
    'Carousel post': i18n.t('platform.content_type.carousel_post', {
      defaultValue:
        'A single Instagram post featuring multiple brand photos or videos that users can swipe through'
    }),
    'Tiktok video': i18n.t('platform.content_type.tiktok_video', {
      defaultValue:
        'Exclusive TikTok video custom-made specifically for the brand.'
    }),
    [LinkedInContentTypeLabel[LinkedInContentType.VIDEO]]: i18n.t(
      'platform.content_type.linkedin_video_post',
      {
        defaultValue:
          'A professional video post where you speak about or demonstrate the brand — such as use cases, expert opinions, or testimonials.'
      }
    ),
    [LinkedInContentTypeLabel[LinkedInContentType.IMAGE_TEXT]]: i18n.t(
      'platform.content_type.linkedin_image_post',
      {
        defaultValue:
          'A carousel post featuring branded visuals, infographics, or professional photos'
      }
    )
  };
  if (key === 'Post' && platform === PlatformItems.TWITTER.toLowerCase()) {
    return i18n.t('platform.content_type.twitter_post', {
      defaultValue: 'Exclusive X post custom-made specifically for the brand.'
    });
  }
  return map?.[key] || '';
};

export const getPlatformPreFinalLinkChecklist = ({
  platform
}: {
  platform: string;
}) => {
  switch (platform.toLowerCase()) {
    case PlatformItems.YOUTUBE.toLowerCase():
      return [
        i18n.t('platform.checklist.youtube.cta_placement', {
          defaultValue:
            'Ensure CTA words and link are placed at the top of the description and visible even when the description collapses. (e.g. Try it now: {promotional link}. Use code {couponcode} for XX% off.)'
        }),
        i18n.t('platform.checklist.youtube.link_usage', {
          defaultValue:
            'Directly copy and use the promotional link provided by AhaCreator, not the official website link or a redirected link.'
        })
      ];
    case PlatformItems.TIKTOK.toLowerCase():
      return [
        i18n.t('platform.checklist.tiktok.cta_caption', {
          defaultValue:
            'Ensure CTA words are mentioned in the caption. (e.g. Check the link in my bio. Use code {couponcode} for XX% off.)'
        }),
        i18n.t('platform.checklist.tiktok.link_placement', {
          defaultValue:
            "The link should be placed at the top of bio. If you're using Linktree, it is preferred to position the link directly above the Linktree in bio. Alternatively, it can be added to the first link within Linktree"
        }),
        i18n.t('platform.checklist.tiktok.link_usage', {
          defaultValue:
            'Directly copy and use the promotional link provided by AhaCreator, not the official website link or a redirected link.'
        })
      ];
    case PlatformItems.INSTAGRAM.toLowerCase():
      return [
        i18n.t('platform.checklist.instagram.cta_caption', {
          defaultValue:
            'Ensure CTA words are mentioned in the caption. (e.g. Check the link in my bio. Use code {couponcode} for XX% off.)'
        }),
        i18n.t('platform.checklist.instagram.link_placement', {
          defaultValue:
            "The link should be placed at the top of bio. If you're using Linktree, it is preferred to position the link directly above the Linktree in bio. Alternatively, it can be added to the first link within Linktree"
        }),
        i18n.t('platform.checklist.instagram.link_usage', {
          defaultValue:
            'Directly copy and use the promotional link provided by AhaCreator, not the official website link or a redirected link.'
        })
      ];
    case PlatformItems.TWITTER.toLowerCase():
      return [
        i18n.t('platform.checklist.twitter.cta_caption', {
          defaultValue:
            'Ensure CTA words and link are in the caption. (e.g. Try it now: {promotional link}. Use code {couponcode} for XX% off.)'
        }),
        i18n.t('platform.checklist.twitter.link_usage', {
          defaultValue:
            'Directly copy and use the promotional link provided by AhaCreator, not the official website link or a redirected link.'
        })
      ];
    case PlatformItems.LINKEDIN.toLowerCase():
      return [
        i18n.t('platform.checklist.linkedin.cta_caption', {
          defaultValue:
            'Ensure CTA words and link are in the caption. (e.g. Try it now: {promotional link}. Use code {couponcode} for XX% off.)'
        }),
        i18n.t('platform.checklist.linkedin.link_usage', {
          defaultValue:
            'Directly copy and use the promotional link provided by AhaCreator, not the official website link or a redirected link.'
        })
      ];
    default:
      return [];
  }
};

export const PlatformIconComponent: React.FC<{
  platform: string;
  className?: string;
}> = ({ platform, className }) => {
  if (PlatformItems.YOUTUBE.toLowerCase() === platform)
    return <YoutubePlatformIcon className={className} />;
  if (PlatformItems.TIKTOK.toLowerCase() === platform)
    return <TiktokPlatformIcon className={className} />;
  if (PlatformItems.INSTAGRAM.toLowerCase() === platform)
    return <InstagramPlatformIcon className={className} />;
  if (PlatformItems.TWITTER.toLowerCase() === platform)
    return <XPlatformIcon className={className} />;
  if (PlatformItems.LINKEDIN.toLowerCase() === platform)
    return <LinkedInPlatformIcon className={className} />;
  if (PlatformItems.FACEBOOK.toLowerCase() === platform)
    return <FaceBookPlatformIcon className={className} />;
  return <div />;
};

export const checkPhylloConfig = (config: any) => {
  // 参数校验
  if (!config.clientDisplayName) {
    toast.error(
      i18n.t('platform.phyllo.errors.client_display_name', {
        defaultValue:
          'The clientDisplayName is empty, please refresh the page or contact customer service.'
      })
    );
    return false;
  }
  if (!config.environment) {
    toast.error(
      i18n.t('platform.phyllo.errors.environment', {
        defaultValue:
          'The environment is empty, please refresh the page or contact customer service.'
      })
    );
    return false;
  }
  if (!config.userId) {
    toast.error(
      i18n.t('platform.phyllo.errors.user_id', {
        defaultValue:
          'The userId is empty, please refresh the page or contact customer service.'
      })
    );
    return false;
  }
  if (!config.token) {
    toast.error(
      i18n.t('platform.phyllo.errors.token', {
        defaultValue:
          'The token is empty, please refresh the page or contact customer service.'
      })
    );
    return false;
  }
  if (config.redirect === true && !config.redirectURL) {
    toast.error(
      i18n.t('platform.phyllo.errors.redirect_url', {
        defaultValue:
          'The redirectURL is empty, please refresh the page or contact customer service.'
      })
    );
    return false;
  }
  if (!config.workPlatformId) {
    toast.error(
      i18n.t('platform.phyllo.errors.work_platform_id', {
        defaultValue:
          'The workPlatformId is empty, please refresh the page or contact customer service.'
      })
    );
    return false;
  }
  return true;
};

export const platformDraftLinkValidator = (url: string, platform: string) => {
  if (platform === AdsPlatform.Youtube) {
    return youtubeLinkValidator(url);
  }
  return linkValidator(url);
};

export const platformFinalLinkValidator = (url: string, platform: string) => {
  if (platform === AdsPlatform.Youtube) {
    return youtubeLinkValidator(url);
  }
  if (platform === AdsPlatform.TikTok) {
    return tiktokLinkValidator(url);
  }
  if (platform === AdsPlatform.Instagram) {
    return instagramReelsLinkValidator(url);
  }
  if (platform === AdsPlatform.Twitter) {
    return twitterLinkValidator(url);
  }
  return linkValidator(url);
};

export const getSubmitModalDesc = (platform: string) => {
  if (platform === AdsPlatform.Youtube) {
    return submitModalDescYoutube;
  } else if (platform === AdsPlatform.TikTok) {
    return submitModalDescTiktok;
  } else if (platform === AdsPlatform.Instagram) {
    return submitModalDescInstagram;
  }
  return submitModalDescDefault;
};

export const getPlatformAudienceTutorialScreenshot = ({
  platform
}: {
  platform: string;
}) => {
  switch (platform.toLowerCase()) {
    case PlatformItems.YOUTUBE.toLowerCase():
      return [
        '/audience-tutorial/Youtube-1.png',
        '/audience-tutorial/Youtube-2.png',
        '/audience-tutorial/Youtube-3.png'
      ];
    case PlatformItems.TIKTOK.toLowerCase():
      return [
        '/audience-tutorial/Tiktok-1.png',
        '/audience-tutorial/Tiktok-2.png',
        '/audience-tutorial/Tiktok-3.png'
      ];
    case PlatformItems.INSTAGRAM.toLowerCase():
      return [
        '/audience-tutorial/Instagram-1.png',
        '/audience-tutorial/Instagram-2.png',
        '/audience-tutorial/Instagram-3.png'
      ];
    case PlatformItems.TWITTER.toLowerCase():
      return [
        '/audience-tutorial/Twitter-1.png',
        '/audience-tutorial/Twitter-2.png',
        '/audience-tutorial/Twitter-3.png'
      ];
    case PlatformItems.LINKEDIN.toLowerCase():
      return [
        '/audience-tutorial/LinkedIN-1.png',
        '/audience-tutorial/LinkedIN-2.png',
        '/audience-tutorial/LinkedIN-3.png',
        '/audience-tutorial/LinkedIN-4.png'
      ];
    default:
      return [];
  }
};

export const getPlatformAudienceTutorialScreenshotStep = ({
  platform
}: {
  platform: string;
}) => {
  switch (platform.toLowerCase()) {
    case PlatformItems.YOUTUBE.toLowerCase():
      return [
        i18n.t('platform.tutorial.youtube.step1', {
          defaultValue: 'Step1: Personal → Customize channel'
        }),
        i18n.t('platform.tutorial.youtube.step2', {
          defaultValue: 'Step2: Analytics → Audience'
        }),
        i18n.t('platform.tutorial.youtube.step3', {
          defaultValue: 'Step3: Extract key information'
        })
      ];
    case PlatformItems.TIKTOK.toLowerCase():
      return [
        i18n.t('platform.tutorial.tiktok.step1', {
          defaultValue: 'Step1: Go to "TikTok Studio"'
        }),
        i18n.t('platform.tutorial.tiktok.step2', {
          defaultValue: 'Step 2: Click "View all"'
        }),
        i18n.t('platform.tutorial.tiktok.step3', {
          defaultValue:
            'Step 3: Take screenshots of "Gender", "Age" and "Locations"'
        })
      ];
    case PlatformItems.INSTAGRAM.toLowerCase():
      return [
        i18n.t('platform.tutorial.instagram.step1', {
          defaultValue: 'Step 1: Go to "Professional dashboard"'
        }),
        i18n.t('platform.tutorial.instagram.step2', {
          defaultValue: 'Step 2: Go to "Views"'
        }),
        i18n.t('platform.tutorial.instagram.step3', {
          defaultValue:
            'Step 3: Take screenshots of "Country", "Age" "Gender" insights'
        })
      ];
    case PlatformItems.TWITTER.toLowerCase():
      return [
        i18n.t('platform.tutorial.twitter.step1', {
          defaultValue: 'Step1: More → Settings and privacy'
        }),
        i18n.t('platform.tutorial.twitter.step2', {
          defaultValue: 'Step2: Premium → Analytics'
        }),
        i18n.t('platform.tutorial.twitter.step3', {
          defaultValue:
            'Step3: Include your channel name, country, gender and age distribution'
        })
      ];
    case PlatformItems.LINKEDIN.toLowerCase():
      return [
        i18n.t('platform.tutorial.linkedin.step1', {
          defaultValue: 'Step1: Click "Show all analytics"'
        }),
        i18n.t('platform.tutorial.linkedin.step2', {
          defaultValue: 'Step2: Click "Followers"'
        }),
        i18n.t('platform.tutorial.linkedin.step3', {
          defaultValue: 'Step3: Click "Show all"'
        }),
        i18n.t('platform.tutorial.linkedin.step4', {
          defaultValue: 'Step4: Include "Location" in the screenshot'
        })
      ];
    default:
      return [];
  }
};
